import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Moon, Sun, Clock, Play, Pause, SkipForward, Volume2, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Slider } from "@/components/ui/slider";
import NeonButton from "@/components/NeonButton";

const sleepPhases = [
  {
    id: "wind-down",
    name: "Wind Down",
    duration: 10, // minutes
    icon: "🌅",
    description: "Gentle transition from alert to relaxed",
    settings: {
      frequency: 432,
      binaural: 10, // Alpha
      noise: "pink",
      brightness: 80,
      volume: 70
    }
  },
  {
    id: "relaxation",
    name: "Deep Relaxation",
    duration: 15,
    icon: "🌙",
    description: "Theta waves for deep calm",
    settings: {
      frequency: 396,
      binaural: 6, // Theta
      noise: "brown",
      brightness: 50,
      volume: 60
    }
  },
  {
    id: "pre-sleep",
    name: "Pre-Sleep",
    duration: 15,
    icon: "😌",
    description: "Preparing body for sleep",
    settings: {
      frequency: 285,
      binaural: 4, // Low Theta
      noise: "brown",
      brightness: 30,
      volume: 50
    }
  },
  {
    id: "deep-sleep",
    name: "Deep Sleep",
    duration: 20,
    icon: "😴",
    description: "Delta waves for restorative rest",
    settings: {
      frequency: 174,
      binaural: 2, // Delta
      noise: "brown",
      brightness: 10,
      volume: 40
    }
  }
];

const SleepMode = ({ 
  onActivate, 
  onDeactivate, 
  onPhaseChange,
  isActive,
  audioContext 
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);
  const [phaseTimeLeft, setPhaseTimeLeft] = useState(0);
  const [totalTimeLeft, setTotalTimeLeft] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [showSettings, setShowSettings] = useState(false);
  const [customDurations, setCustomDurations] = useState(
    sleepPhases.reduce((acc, phase) => ({ ...acc, [phase.id]: phase.duration }), {})
  );
  
  const timerRef = useRef(null);
  const brightnessRef = useRef(null);

  const totalDuration = Object.values(customDurations).reduce((a, b) => a + b, 0);

  const currentPhase = sleepPhases[currentPhaseIndex];

  const startSleepMode = useCallback(() => {
    setIsRunning(true);
    setCurrentPhaseIndex(0);
    setPhaseTimeLeft(customDurations[sleepPhases[0].id] * 60);
    setTotalTimeLeft(totalDuration * 60);
    setBrightness(sleepPhases[0].settings.brightness);
    
    onActivate?.();
    onPhaseChange?.(sleepPhases[0]);
  }, [customDurations, totalDuration, onActivate, onPhaseChange]);

  const stopSleepMode = useCallback(() => {
    setIsRunning(false);
    setCurrentPhaseIndex(0);
    setPhaseTimeLeft(0);
    setBrightness(100);
    
    if (timerRef.current) clearInterval(timerRef.current);
    if (brightnessRef.current) clearInterval(brightnessRef.current);
    
    onDeactivate?.();
  }, [onDeactivate]);

  const skipToNextPhase = useCallback(() => {
    if (currentPhaseIndex < sleepPhases.length - 1) {
      const nextIndex = currentPhaseIndex + 1;
      setCurrentPhaseIndex(nextIndex);
      setPhaseTimeLeft(customDurations[sleepPhases[nextIndex].id] * 60);
      setBrightness(sleepPhases[nextIndex].settings.brightness);
      onPhaseChange?.(sleepPhases[nextIndex]);
    } else {
      stopSleepMode();
    }
  }, [currentPhaseIndex, customDurations, onPhaseChange, stopSleepMode]);

  // Main timer
  useEffect(() => {
    if (!isRunning) return;

    timerRef.current = setInterval(() => {
      setPhaseTimeLeft(prev => {
        if (prev <= 1) {
          skipToNextPhase();
          return 0;
        }
        return prev - 1;
      });
      
      setTotalTimeLeft(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRunning, skipToNextPhase]);

  // Gradual brightness transition
  useEffect(() => {
    if (!isRunning || !currentPhase) return;

    const targetBrightness = currentPhase.settings.brightness;
    const step = (brightness - targetBrightness) / 30; // 30 second transition

    if (Math.abs(brightness - targetBrightness) > 1) {
      brightnessRef.current = setInterval(() => {
        setBrightness(prev => {
          const next = prev - step;
          if ((step > 0 && next <= targetBrightness) || (step < 0 && next >= targetBrightness)) {
            clearInterval(brightnessRef.current);
            return targetBrightness;
          }
          return next;
        });
      }, 1000);
    }

    return () => {
      if (brightnessRef.current) clearInterval(brightnessRef.current);
    };
  }, [isRunning, currentPhaseIndex]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const updatePhaseDuration = (phaseId, duration) => {
    setCustomDurations(prev => ({ ...prev, [phaseId]: duration }));
  };

  return (
    <div className="space-y-6">
      {/* Dimming overlay when active */}
      <AnimatePresence>
        {isRunning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 - brightness / 100 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black pointer-events-none z-40"
            style={{ opacity: (100 - brightness) / 100 * 0.7 }}
          />
        )}
      </AnimatePresence>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Moon size={18} className="text-indigo-400" />
          <h4 className="text-sm font-medium text-gray-300">Sleep Mode</h4>
          {isRunning && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 animate-pulse">
              Active
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500">
          Total: {totalDuration} min
        </span>
      </div>

      {/* Phase Progress */}
      {isRunning && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-4 border-indigo-500/30"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{currentPhase?.icon}</span>
              <div>
                <h5 className="text-white font-medium">{currentPhase?.name}</h5>
                <p className="text-xs text-gray-500">{currentPhase?.description}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-mono text-white">{formatTime(phaseTimeLeft)}</div>
              <div className="text-xs text-gray-500">Phase {currentPhaseIndex + 1}/{sleepPhases.length}</div>
            </div>
          </div>

          {/* Phase progress bar */}
          <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-3">
            <motion.div
              className="h-full bg-gradient-to-r from-indigo-500 to-violet-500"
              style={{
                width: `${((customDurations[currentPhase?.id] * 60 - phaseTimeLeft) / (customDurations[currentPhase?.id] * 60)) * 100}%`
              }}
            />
          </div>

          {/* Total progress */}
          <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
            <span>Total remaining: {formatTime(totalTimeLeft)}</span>
            <span>Brightness: {Math.round(brightness)}%</span>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-3">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={stopSleepMode}
              className="p-3 rounded-full bg-red-500/20 text-red-400 hover:bg-red-500/30"
            >
              <Pause size={20} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={skipToNextPhase}
              disabled={currentPhaseIndex >= sleepPhases.length - 1}
              className="p-3 rounded-full bg-white/10 text-white hover:bg-white/20 disabled:opacity-50"
            >
              <SkipForward size={20} />
            </motion.button>
          </div>
        </motion.div>
      )}

      {/* Phase Timeline */}
      {!isRunning && (
        <>
          <div className="space-y-2">
            {sleepPhases.map((phase, index) => (
              <motion.div
                key={phase.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-3"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{phase.icon}</span>
                    <div>
                      <h5 className="text-sm font-medium text-white">{phase.name}</h5>
                      <p className="text-xs text-gray-500">{phase.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Slider
                      value={[customDurations[phase.id]]}
                      onValueChange={([v]) => updatePhaseDuration(phase.id, v)}
                      min={5}
                      max={60}
                      step={5}
                      className="w-20"
                    />
                    <span className="text-xs text-violet-400 font-mono w-12">
                      {customDurations[phase.id]}m
                    </span>
                  </div>
                </div>
                
                {/* Phase settings preview */}
                <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                  <span>{phase.settings.frequency}Hz</span>
                  <span>•</span>
                  <span>{phase.settings.binaural}Hz binaural</span>
                  <span>•</span>
                  <span>{phase.settings.noise} noise</span>
                  <span>•</span>
                  <span>{phase.settings.brightness}% brightness</span>
                </div>
              </motion.div>
            ))}
          </div>

          <NeonButton 
            onClick={startSleepMode} 
            className="w-full"
            data-testid="start-sleep-mode"
          >
            <Moon size={18} className="mr-2" />
            Start Sleep Journey ({totalDuration} min)
          </NeonButton>
        </>
      )}
    </div>
  );
};

export default SleepMode;
